import { useQuery } from '@apollo/client';
import {
  getQueryParameters,
  parseQueryString,
  updateQueryString,
  OptionalString,
  createQueryString,
} from '@sixfold/app-utils';
import { InfiniteTableSortBy, Stack } from '@sixfold/common-ui';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { InfiniteScroll } from '../../components/infinitescroll';
import {
  TourListQuery,
  TourListFilterValuesQuery,
  TourListFilterValuesQueryVariables,
  getNodes,
  loadMoreFromConnection,
  TourListQueryVariables,
  TourStatusFilter,
  PAGINATION_PAGE_SIZE,
  SortDirection,
  TourSortKey,
  TourDelayStatusFilter,
  TourTextSearchField,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { TourFilters } from '../components/tour_filters';
import { TourList, Tour, FormattedTour } from '../components/tour_list';
import { tourListQuery, tourListFilterValuesQuery } from '../graphql';
import { getTransportModesFromFilter } from '../utils';

class LoadingContainer extends Loader<TourListQuery, { data: { tours: Tour[] } }, TourListQueryVariables> {}

function convertTourSortKey(key: string | undefined): TourSortKey | undefined {
  switch (key) {
    case 'start_time':
      return TourSortKey.START_TIME;
    case 'shipper_name':
      return TourSortKey.SHIPPER_NAME;
    case 'carrier_name':
      return TourSortKey.CARRIER_NAME;
    case 'license_plate':
      return TourSortKey.VEHICLE_LICENSE_PLATE_NUMBER;
    default:
      return undefined;
  }
}

function querySortOrder(sortKey: string | undefined, sortDirection: string | undefined) {
  const key = convertTourSortKey(sortKey);
  const direction = sortDirection === 'asc' ? SortDirection.ASCENDING : SortDirection.DESCENDING;

  if (key === undefined) return;
  return { key, direction };
}

function getSortByValue(
  key: string | undefined,
  direction: string | undefined,
): InfiniteTableSortBy<keyof FormattedTour> | undefined {
  if (key === undefined) return undefined;
  return {
    columnId: key as keyof FormattedTour,
    order: direction === 'asc' ? 'ASC' : 'DESC',
  };
}

export const TourListContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const parsedQueryString = parseQueryString({
    queryParametersToParse: {
      status: OptionalString,
      delay: OptionalString,
      shipper_id: OptionalString,
      carrier_id: OptionalString,
      platform_id: OptionalString,
      platform_branch_id: OptionalString,
      place_id: OptionalString,
      vehicle_id: OptionalString,
      start_time_start: OptionalString,
      start_time_end: OptionalString,
      sort_direction: OptionalString,
      sort_key: OptionalString,
      search: OptionalString,
      search_field: OptionalString,
      transport_mode: OptionalString,
    },
    queryString: location.search,
  });

  const {
    status,
    delay,
    shipper_id,
    carrier_id,
    vehicle_id,
    platform_id,
    place_id,
    start_time_start,
    start_time_end,
    sort_direction,
    sort_key,
    search,
    search_field,
    platform_branch_id,
    transport_mode,
  } = parsedQueryString;

  const shouldExecuteQuery = Object.keys(parsedQueryString).some((key) => parsedQueryString[key] !== undefined);

  const handleFilterToggle = useCallback(
    ({ name, value }: { name: string; value: string | undefined }) => {
      return navigate(
        `${location.pathname}?${updateQueryString(location.search, {
          param: name,
          value,
        })}`,
      );
    },
    [navigate, location],
  );

  const handleClearFilters = useCallback(() => {
    return navigate(location.pathname);
  }, [navigate, location.pathname]);

  const handleSortChange = useCallback(
    (newSort: InfiniteTableSortBy<keyof FormattedTour>) => {
      const qs = getQueryParameters(location.search);
      return navigate({
        ...location,
        search: createQueryString({
          ...qs,
          sort_key: newSort.columnId as string,
          sort_direction: newSort.order.toLowerCase(),
        }),
      });
    },
    [navigate, location],
  );

  const filterValuesResult = useQuery<TourListFilterValuesQuery, TourListFilterValuesQueryVariables>(
    tourListFilterValuesQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        vehicle_id,
        hasVehicleFilter: vehicle_id !== undefined,
        shipperId: shipper_id,
        hasShipperFilter: shipper_id !== undefined,
      },
    },
  );

  const tourListResult = useQuery<TourListQuery, TourListQueryVariables>(tourListQuery, {
    skip: !shouldExecuteQuery,
    variables: {
      first: PAGINATION_PAGE_SIZE,
      status: status === undefined ? undefined : (status.toUpperCase() as TourStatusFilter),
      shipper_ids: shipper_id === undefined ? undefined : [shipper_id],
      carrier_ids: carrier_id === undefined ? undefined : [carrier_id],
      platform_id,
      platform_branch_id,
      place_ids: place_id === undefined ? undefined : [place_id],
      vehicle_id,
      delayStatus: delay === undefined ? undefined : (delay.toUpperCase() as TourDelayStatusFilter),
      start_time_start: start_time_start !== undefined ? new Date(start_time_start).toISOString() : undefined,
      start_time_end: start_time_end !== undefined ? new Date(start_time_end).toISOString() : undefined,
      sortOrder: querySortOrder(sort_key, sort_direction),
      textSearch:
        search !== undefined && search.length >= 3
          ? {
              query: search,
              field:
                search_field !== undefined
                  ? (search_field.toUpperCase() as TourTextSearchField)
                  : TourTextSearchField.ALL_EXTENDED,
            }
          : undefined,
      transportMode: getTransportModesFromFilter(transport_mode),
    },
  });

  const platforms = getNodes(filterValuesResult.data?.platforms ?? undefined);
  const places = getNodes(filterValuesResult.data?.company?.placesConnection.places ?? undefined);
  const vehicle = filterValuesResult.data?.vehicle ?? null;

  return (
    <Stack direction="column" gap="medium" style={{ height: 'calc(100vh - 100px)' }}>
      <TourFilters
        filters={{ platforms, places, vehicle }}
        activeFilters={{
          status,
          delay,
          shipper_id,
          carrier_id,
          platform_id,
          platform_branch_id,
          place_id,
          vehicle_id,
          start_time_start,
          start_time_end,
          search,
          search_field,
          transport_mode,
        }}
        onClearFilters={handleClearFilters}
        onFilterToggle={handleFilterToggle}
      />
      {!shouldExecuteQuery && <>Please specify at least one filter to see tours</>}
      <LoadingContainer
        result={tourListResult}
        mapData={({ data }) => ({
          data: {
            tours: getNodes(data.tours?.tours),
          },
        })}>
        {({ data }) => (
          <InfiniteScroll
            className="tour__tour-list"
            triggerOnWindow={false}
            loadMoreEntries={() => loadMoreFromConnection(tourListQuery, tourListResult, ['tours', 'tours'])}>
            <TourList data={data} onSortChange={handleSortChange} sortBy={getSortByValue(sort_key, sort_direction)} />
          </InfiniteScroll>
        )}
      </LoadingContainer>
    </Stack>
  );
};
